import { 
  ChevronDown, HelpCircle, Shield, Clock, Award, Users, Repeat, CheckCircle2, DollarSign, Target, 
  Package, Layers, MessageCircle, Info, AlertCircle, FileQuestion, Lightbulb, TrendingUp, Zap, 
  Star, Sparkles, Heart, ThumbsUp, Rocket, Gift, Trophy, Crown, Flag, Compass, Map, Navigation, 
  Globe, Lock, Key, Settings, Wrench, Box, ShoppingCart, CreditCard, BarChart, PieChart, 
  Activity, Calendar, FileText, BookOpen, Bookmark, Archive, Palette, File
} from 'lucide-react';
import { apiFetch } from '../../utils/api';
import { logger } from '../../utils/logger';
import React, { useState, useEffect } from 'react';

const iconMap: { [key: string]: any } = {
  HelpCircle, MessageCircle, Info, AlertCircle, FileQuestion, Lightbulb, Shield, Award, Target,
  TrendingUp, Users, Zap, Clock, DollarSign, CheckCircle2, Star, Sparkles, Heart, ThumbsUp,
  Rocket, Gift, Trophy, Crown, Flag, Compass, Map, Navigation, Globe, Lock, Key, Settings,
  Wrench, Box, Package, ShoppingCart, CreditCard, BarChart, PieChart, Activity, Calendar,
  FileText, BookOpen, Bookmark, Archive, Repeat, Layers, Palette, File
};

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  order: number;
  open?: boolean;
  icon?: string;
  iconColor?: string;
}

interface FAQData {
  id: string;
  badgeText: string;
  badgeIcon: string;
  sectionTitle: string;
  sectionSubtitle: string;
  faqs: FAQItem[];
  visible: boolean;
  updatedAt: string;
}

// ============================================
// SKELETON
// ============================================

const FAQSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
    <div className="max-w-4xl mx-auto space-y-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-44 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-80 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>

      {/* FAQ accordion items — 6 matching default data */}
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white/80 rounded-2xl border border-gray-100 p-5 sm:p-6">
            <div className="flex items-center gap-4">
              {/* Icon */}
              <div className="w-10 h-10 flex-shrink-0 bg-gray-200 rounded-xl relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              {/* Question — vary widths for realism */}
              <div
                className="h-5 bg-gray-200 rounded flex-1 relative overflow-hidden"
                style={{ maxWidth: `${65 + (i % 3) * 12}%` }}
              >
                <div className="absolute inset-0 shimmer" />
              </div>
              {/* Chevron */}
              <div className="w-9 h-9 flex-shrink-0 bg-gray-200 rounded-lg relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [sectionData, setSectionData] = useState<FAQData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQSection();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        logger.log('🔄 [FAQ SECTION] Tab visible - refetching data...');
        fetchFAQSection();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchFAQSection = async () => {
    try {
      const timestamp = new Date().getTime();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await apiFetch(
        `content/faqs?t=${timestamp}`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        logger.log('ℹ️ [FAQ SECTION] Backend not available, using default content');
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        logger.log('✅ [FAQ SECTION] Section data loaded from CMS:', result.data);
        setSectionData(result.data);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        logger.log('ℹ️ [FAQ SECTION] Backend not responding');
      } else if (error.message?.includes('fetch')) {
        logger.log('ℹ️ [FAQ SECTION] Backend endpoint not available yet');
      } else {
        logger.log('ℹ️ [FAQ SECTION] Could not fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const badgeText   = sectionData?.badgeText     || 'Common Questions';
  const badgeIcon   = sectionData?.badgeIcon     || 'HelpCircle';
  const title       = sectionData?.sectionTitle  || 'Frequently Asked Questions';
  const description = sectionData?.sectionSubtitle || 'Clear answers for manufacturing leaders evaluating 3D animation solutions.';
  const visible     = sectionData?.visible !== false;

  const defaultItems: FAQItem[] = [
    { id: '1', question: 'What does a typical 3D animation project cost?', answer: 'Costs vary depending on complexity, length, and customization. Simple product demos typically range from $5,000-$8,000, while complex process visualizations can be $15,000-$25,000. We provide transparent quotes after understanding your project requirements.', order: 1 },
    { id: '2', question: 'What is the usual project timeline?', answer: 'Most projects are delivered in 4–8 weeks, depending on scope. This includes discovery, scripting, modeling, animation, review cycles, and final production. Rush delivery options are available for time-sensitive projects.', order: 2 },
    { id: '3', question: 'How accurate are the animations?', answer: 'Our team collaborates with your engineers and product experts to ensure technical precision and realistic visuals. We review CAD files, technical specifications, and work closely with your team throughout the process to guarantee accuracy.', order: 3 },
    { id: '4', question: 'Will our proprietary data remain secure?', answer: 'Yes. We sign NDAs and follow strict confidentiality protocols. All CAD files, designs, and sensitive data are handled securely. Your intellectual property is protected throughout the entire process.', order: 4 },
    { id: '5', question: 'How many revisions are included?', answer: 'Structured feedback rounds are included in every project to guarantee satisfaction while maintaining schedule and cost transparency. We typically include 2-3 revision rounds at key milestones (storyboard, animation preview, and final review).', order: 5 },
    { id: '6', question: 'Can these videos be used across multiple departments?', answer: 'Absolutely. Our animations are designed to deliver value across sales, marketing, customer support, and employee training. You receive the video in multiple formats optimized for different use cases, maximizing your investment.', order: 6 },
  ];

  const items = sectionData?.faqs || defaultItems;
  const BadgeIcon = iconMap[badgeIcon] || HelpCircle;

  // Show skeleton while loading
  if (loading) {
    return <FAQSkeleton />;
  }

  // Don't render if section is hidden
  if (!visible) return null;

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      {/* Premium Background Accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-[#E8B84D]/10 via-[#E87D3E]/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#2F2E5C]/10 via-[#D74444]/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute inset-0 opacity-[0.015]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{badgeText}</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
            {title}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => {
            const ItemIcon = item.icon ? (iconMap[item.icon] || HelpCircle) : HelpCircle;
            const hasCustomIcon = !!item.icon;

            let customIconColor = item.iconColor || '#D74444';
            if (customIconColor && !customIconColor.startsWith('#')) {
              customIconColor = '#' + customIconColor;
            }

            const gradients = [
              'from-[#D74444] to-[#e85e5e]',
              'from-[#E87D3E] to-[#E8B84D]',
              'from-[#2F2E5C] to-[#4a487a]',
              'from-[#D74444] to-[#e85e5e]',
              'from-[#E87D3E] to-[#E8B84D]',
              'from-[#2F2E5C] to-[#4a487a]',
            ];
            const gradient = gradients[index % gradients.length];

            return (
              <div
                key={item.id}
                className="group relative bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:border-gray-200 transition-all duration-300"
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-0 group-hover:opacity-[0.02] transition-opacity duration-300`} />

                <button
                  className="relative w-full flex items-center justify-between p-5 sm:p-6 text-left group"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <div className="flex items-start gap-4 flex-1 pr-6">
                    {hasCustomIcon ? (
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300"
                        style={{ backgroundColor: customIconColor }}
                      >
                        <ItemIcon className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <div className={`flex-shrink-0 w-10 h-10 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <ItemIcon className="w-5 h-5" />
                      </div>
                    )}

                    <span className="font-bold text-sm sm:text-base text-[#2F2E5C] leading-relaxed pt-1.5">
                      {item.question}
                    </span>
                  </div>

                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    openIndex === index
                      ? `bg-gradient-to-br ${gradient}`
                      : 'bg-gray-100 group-hover:bg-gray-200'
                  }`}>
                    <ChevronDown
                      className={`w-5 h-5 transition-all duration-300 ${
                        openIndex === index ? 'rotate-180 text-white' : 'text-gray-600'
                      }`}
                    />
                  </div>
                </button>

                {openIndex === index && (
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 pl-[4.5rem] animate-in fade-in slide-in-from-top-2 duration-300">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}